import { useId, useRef, useState, type KeyboardEvent, type PointerEvent } from "react";
import { Link, useNavigate } from "react-router";
import { useQueryClient } from "@tanstack/react-query";
import type { Category, Channel, CommunityStructure, VoiceParticipant } from "@atrium/shared";
import { api, errorMessage } from "../../lib/api";
import { cn } from "../../lib/cn";
import { useUI } from "../../lib/uiStore";
import { Avatar } from "../../components/ui/Avatar";
import { CommunityBadges } from "../../components/UserBadges";
import { IconButton } from "../../components/ui/Button";
import { Badge } from "../../components/ui/Controls";
import { confirm } from "../../components/ui/Dialog";
import { Menu, MenuContent, MenuItem, MenuSeparator, MenuTrigger } from "../../components/ui/Menu";
import { toast } from "../../components/ui/Toast";
import { Tooltip } from "../../components/ui/Tooltip";
import {
  IconAnnouncement,
  IconBack,
  IconChevronDown,
  IconChevronRight,
  IconCompose,
  IconFolder,
  IconGlobe,
  IconHash,
  IconLock,
  IconLogout,
  IconMicOff,
  IconDeafened,
  IconPlus,
  IconSettings,
  IconUserPlus,
  IconVolume,
  IconShield,
  IconTimer,
} from "../../components/icons";
import { IconForum, IconMediaChannel } from "../../components/icons";
import { IconStage } from "../../components/icons";
import { LiveStreamBadge } from "../voice/LiveStreamBadge";
import { CommunityBanner } from "./CommunityBanner";
import { useMe } from "../auth/AuthProvider";
import { permsOf } from "./hooks";
import { ChannelDialog, CategoryDialog } from "./ChannelDialogs";
import { InviteDialog } from "./InviteDialog";
import { useVoice, useVoiceStates } from "../voice/VoiceProvider";
import { UserPanel } from "../voice/UserPanel";
import { measure, rowAt, usePointerDrag } from "../rail/usePointerDrag";
import { DragLayer } from "../rail/DragLayer";
import { channelLocation, moveCategory, moveChannel, stepChannel, useStructureOrder } from "./channelOrder";
import { ChannelContextMenu } from "./ChannelContextMenu";
import { CategoryContextMenu } from "./CategoryContextMenu";
import { offerPermissionSync } from "./permissionSync";

type SortSource = { kind: "channel" | "category"; id: string };
type SortTarget =
  | { type: "channel"; categoryId: string | null; index: number; y: number; /** Highlight a category header or empty slot instead of a line. */ into?: string }
  | { type: "category"; index: number; y: number; into?: undefined };

export function channelIcon(c: Pick<Channel, "type">) {
  if (c.type === "forum") return IconForum;
  if (c.type === "media") return IconMediaChannel;
  if (c.type === "stage") return IconStage;
  return c.type === "voice" ? IconVolume : c.type === "announcement" ? IconAnnouncement : IconHash;
}

/** Lock after a private channel's name, in the sidebar and channel headers. */
export function PrivateMark({ className }: { className?: string }) {
  return (
    <Tooltip label="Private channel">
      <IconLock className={cn("icon size-3.5 shrink-0 opacity-70", className)} aria-label="Private channel" />
    </Tooltip>
  );
}

function VoiceParticipants({ list, communityId }: { list: VoiceParticipant[]; communityId: string }) {
  const { speaking } = useVoice();
  if (!list.length) return null;
  return (
    <ul className="mb-1 ml-8 space-y-0.5">
      {list.map((p) => (
        <li key={p.socketId} className="flex h-7 items-center gap-2 rounded-md px-1.5 text-sm text-fg-2">
          <span className={cn("rounded-full ring-2 transition-shadow", speaking.has(p.socketId) ? "ring-success" : "ring-transparent")}>
            <Avatar src={p.user.avatarUrl} name={p.user.displayName} size="xs" />
          </span>
          <span className="flex-1 truncate">{p.user.displayName}</span>
          {p.streaming && <LiveStreamBadge p={p} communityId={communityId} participants={list} />}
          {p.deafened ? (
            <IconDeafened className="icon size-3.5 text-fg-3" aria-label="Deafened" />
          ) : p.serverMuted && p.speaker !== false ? (
            <IconMicOff className="icon size-3.5 text-danger" aria-label="Muted by a moderator" />
          ) : p.muted && p.speaker !== false ? (
            <IconMicOff className="icon size-3.5 text-fg-3" aria-label="Muted" />
          ) : null}
        </li>
      ))}
    </ul>
  );
}

function ChannelItem({
  channel,
  active,
  canManage,
  onEdit,
  participants,
  index,
  canDelete,
  dnd,
}: {
  channel: Channel;
  active: boolean;
  canManage: boolean;
  onEdit: () => void;
  participants: VoiceParticipant[];
  index: number;
  canDelete: boolean;
  /** Present for members who can reorder channels. */
  dnd?: { dragging: boolean; onPointerDown: (e: PointerEvent) => void; onKeyDown: (e: KeyboardEvent) => void; hintId: string };
}) {
  const Icon = channelIcon(channel);
  const voice = useVoice();
  const navigate = useNavigate();
  const unread = channel.unread && !active;
  const inThisRoom = voice.channelId === channel.id;
  return (
    <li data-ch-row data-id={channel.id} data-cat={channel.categoryId ?? ""} data-index={index} className={cn(dnd?.dragging && "opacity-40")}>
      <ChannelContextMenu channel={channel} canEdit={canManage} canDelete={canDelete} active={active}>
      <div className="group relative">
        {unread && <span aria-hidden className="absolute -left-2 top-1/2 h-2 w-1 -translate-y-1/2 rounded-r-full bg-fg" />}
        <Link
          to={`/c/${channel.communityId}/${channel.id}`}
          data-ch-focus
          aria-describedby={dnd?.hintId}
          onPointerDown={(e) => {
            if (!(e.target as Element).closest("button")) dnd?.onPointerDown(e);
          }}
          onKeyDown={dnd?.onKeyDown}
          onClick={(e) => {
            if (channel.type === "voice" || channel.type === "stage") {
              e.preventDefault();
              navigate(`/c/${channel.communityId}/${channel.id}`);
              if (!inThisRoom) void voice.join(channel.id, channel.communityId, channel.name);
            }
          }}
          aria-current={active ? "page" : undefined}
          className={cn(
            "flex h-8 items-center gap-1.5 rounded-md pl-2 pr-1.5 text-base transition-colors",
            dnd && "[-webkit-touch-callout:none]",
            active ? "bg-surface-2 text-fg" : unread ? "font-semibold text-fg hover:bg-surface" : "text-fg-3 hover:bg-surface hover:text-fg-2",
          )}
        >
          <Icon className="icon size-[18px] shrink-0 opacity-80" aria-hidden />
          <span className="min-w-0 truncate">{channel.name}</span>
          {channel.isPrivate && <PrivateMark />}
          <span className="flex-1" />
          {channel.type === "voice" && channel.userLimit > 0 && (
            <span className="rounded bg-surface-2 px-1 text-xs text-fg-3 tabular" aria-label={`${participants.length} of ${channel.userLimit}`}>
              {participants.length}/{channel.userLimit}
            </span>
          )}
          {channel.mentionCount > 0 && !active && <Badge count={channel.mentionCount} />}
          {canManage && (
            <Tooltip label="Edit channel">
              <button
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onEdit();
                }}
                aria-label={`Edit ${channel.name}`}
                className="hidden rounded p-0.5 text-fg-3 hover:text-fg group-hover:block group-focus-within:block"
              >
                <IconSettings className="icon size-4" />
              </button>
            </Tooltip>
          )}
        </Link>
      </div>
      </ChannelContextMenu>
      {(channel.type === "voice" || channel.type === "stage") && <VoiceParticipants list={participants} communityId={channel.communityId} />}
    </li>
  );
}

export function ChannelSidebar({ structure, activeChannelId, mobile }: { structure: CommunityStructure; activeChannelId?: string; mobile?: boolean }) {
  const me = useMe();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { community, categories, channels } = structure;
  const perms = permsOf(community.myPermissions, community.isOwner);
  const voiceStates = useVoiceStates(community.id, structure.voiceStates);
  const openComposer = useUI((s) => s.openComposer);
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});
  const [channelDialog, setChannelDialog] = useState<{ open: boolean; channel?: Channel; categoryId?: string | null }>({ open: false });
  const [categoryDialog, setCategoryDialog] = useState<{ open: boolean; category?: Category }>({ open: false });
  const [inviteOpen, setInviteOpen] = useState(false);

  const leave = async () => {
    const ok = await confirm({ title: `Leave ${community.name}?`, description: community.visibility === "private" ? "You'll need a new invite to rejoin." : undefined, confirmLabel: "Leave", danger: true });
    if (!ok) return;
    try {
      await api.post(`/api/communities/${community.id}/leave`);
      await qc.invalidateQueries({ queryKey: ["my-communities"] });
      qc.removeQueries({ queryKey: ["structure", community.id] });
      navigate("/c");
    } catch (e) {
      toast.error(errorMessage(e));
    }
  };

  const groups: { category: Category | null; channels: Channel[] }[] = [
    { category: null, channels: channels.filter((c) => !c.categoryId) },
    ...categories.map((cat) => ({ category: cat, channels: channels.filter((c) => c.categoryId === cat.id) })),
  ];

  /* ── Drag and drop / Alt+↑↓ reordering (Manage channels only) ── */
  const canReorder = perms.manageChannels;
  const order = useStructureOrder(community.id);
  const navRef = useRef<HTMLElement>(null);
  const hintId = useId();
  const [announce, setAnnounce] = useState("");
  const nameOf = (s: SortSource) => (s.kind === "category" ? categories.find((c) => c.id === s.id)?.name : channels.find((c) => c.id === s.id)?.name) ?? "Item";

  const hitTest = (_x: number, y: number, src: SortSource): SortTarget | null => {
    const root = navRef.current;
    if (!root) return null;
    if (src.kind === "category") {
      const b = rowAt(measure(root, "[data-cat-block]:not([data-cat-block=''])"), y);
      if (!b) return null;
      const i = Number(b.el.dataset.index);
      const from = categories.findIndex((c) => c.id === src.id);
      const t = y < (b.rect.top + b.rect.bottom) / 2 ? { index: i, y: b.rect.top - 6 } : { index: i + 1, y: b.rect.bottom - 6 };
      return t.index === from || t.index === from + 1 ? null : { type: "category", ...t };
    }
    const r = rowAt(measure(root, "[data-ch-row],[data-cat-row],[data-ch-empty]"), y);
    if (!r) return null;
    const cat = r.el.dataset.cat || null;
    let t: { categoryId: string | null; index: number; y: number; into?: string };
    const count = (cid: string | null) => channels.filter((c) => (c.categoryId ?? null) === cid).length;
    if (r.el.dataset.chEmpty !== undefined) t = { categoryId: cat, index: 0, y: (r.rect.top + r.rect.bottom) / 2, into: cat ?? "" };
    else if (r.el.dataset.catRow !== undefined && y < (r.rect.top + r.rect.bottom) / 2) {
      // Above a category header: the end of the group before it (no category, for the first one).
      const k = categories.findIndex((c) => c.id === cat);
      const prev = k > 0 ? categories[k - 1].id : null;
      t = { categoryId: prev, index: count(prev), y: r.rect.top - 6 };
    } else if (r.el.dataset.catRow !== undefined)
      t = cat && collapsed[cat] ? { categoryId: cat, index: count(cat), y: r.rect.bottom, into: cat } : { categoryId: cat, index: 0, y: r.rect.bottom + 1 };
    else {
      const i = Number(r.el.dataset.index);
      t = y < (r.rect.top + r.rect.bottom) / 2 ? { categoryId: cat, index: i, y: r.rect.top } : { categoryId: cat, index: i + 1, y: r.rect.bottom };
    }
    const loc = channelLocation(structure, src.id);
    if (loc && loc.categoryId === t.categoryId && (t.index === loc.index || t.index === loc.index + 1)) return null;
    return { type: "channel", ...t };
  };

  const commitChannel = (id: string, to: { categoryId: string | null; index: number }) => {
    const r = moveChannel(structure, id, to);
    if (!r) return false;
    const from = channels.find((c) => c.id === id)?.categoryId ?? null;
    void order.channels(r).then((ok) => {
      // Into a different category: ask whether to take on its permissions.
      const moved = channels.find((c) => c.id === id);
      if (ok && moved && to.categoryId && to.categoryId !== from) void offerPermissionSync(qc, structure, { ...moved, categoryId: to.categoryId }, to.categoryId);
    });
    const cat = categories.find((c) => c.id === to.categoryId);
    setAnnounce(`${nameOf({ kind: "channel", id })} moved${cat ? ` to ${cat.name}` : ""}`);
    if (to.categoryId) setCollapsed((c) => ({ ...c, [to.categoryId!]: false }));
    return true;
  };
  const commitCategory = (id: string, index: number) => {
    const r = moveCategory(structure, id, index);
    if (!r) return false;
    void order.categories(r);
    setAnnounce(`${nameOf({ kind: "category", id })} moved`);
    return true;
  };
  const onDrop = (src: SortSource, t: SortTarget) => (t.type === "category" ? commitCategory(src.id, t.index) : commitChannel(src.id, t));
  const { drag, pointerDown } = usePointerDrag<SortSource, SortTarget>({ hitTest, onDrop, scrollRef: navRef, enabled: canReorder });

  const keyMove = (e: KeyboardEvent, src: SortSource) => {
    if (!canReorder || !e.altKey || (e.key !== "ArrowUp" && e.key !== "ArrowDown")) return;
    e.preventDefault();
    const dir = e.key === "ArrowUp" ? -1 : 1;
    if (src.kind === "category") {
      const from = categories.findIndex((c) => c.id === src.id);
      if (!commitCategory(src.id, dir < 0 ? from - 1 : from + 2)) return;
    } else {
      const to = stepChannel(structure, src.id, dir);
      if (!to || !commitChannel(src.id, to)) return;
    }
    const sel = src.kind === "category" ? `[data-cat-row][data-cat="${src.id}"] button` : `[data-ch-row][data-id="${src.id}"] [data-ch-focus]`;
    requestAnimationFrame(() => navRef.current?.querySelector<HTMLElement>(sel)?.focus());
  };
  const dndFor = (s: SortSource) =>
    canReorder ? { dragging: drag?.source.id === s.id, onPointerDown: (e: PointerEvent) => pointerDown(e, s), onKeyDown: (e: KeyboardEvent) => keyMove(e, s), hintId } : undefined;
  const dropInto = drag?.target?.type === "channel" ? drag.target.into : undefined;
  const navRect = drag ? navRef.current?.getBoundingClientRect() : null;

  return (
    <div className={cn("flex h-full flex-col bg-bg-subtle", !mobile && "w-64 border-r border-line xl:w-72")}>
      {/* Only image banners earn the space here; a flat colour strip is decoration. */}
      {community.showBannerInSidebar && community.bannerUrl && (
        <CommunityBanner url={community.bannerUrl} preset={community.bannerColor} className="h-20 shrink-0" />
      )}
      <div className="flex h-12 shrink-0 items-center border-b border-line">
        {mobile && <IconButton label="Back to communities" icon={IconBack} className="ml-1.5" tooltip={false} onClick={() => navigate("/c")} />}
      <Menu>
        <MenuTrigger asChild>
          <button className="flex h-full min-w-0 flex-1 items-center gap-2 px-3 text-left hover:bg-surface/60" aria-label="Community menu">
            <span className="flex min-w-0 flex-1 items-center gap-1.5">
              <span className="truncate text-base font-semibold text-fg">{community.name}</span>
              <CommunityBadges badges={community.badges} />
            </span>
            {community.visibility === "private" ? (
              <IconLock className="icon size-4 text-fg-3" aria-label="Private community" />
            ) : null}
            <IconChevronDown className="icon size-4 text-fg-3" aria-hidden />
          </button>
        </MenuTrigger>
        <MenuContent align="start" className="w-56">
          {perms.invite && (
            <MenuItem icon={IconUserPlus} onSelect={() => setInviteOpen(true)}>
              Invite people
            </MenuItem>
          )}
          {perms.publish && community.visibility === "public" && community.publishToFeed && (
            <MenuItem icon={IconCompose} onSelect={() => openComposer({ community: { id: community.id, name: community.name } })}>
              New announcement
            </MenuItem>
          )}
          {perms.anyAdmin && (
            <MenuItem icon={IconSettings} onSelect={() => navigate(`/c/${community.id}/settings`)}>
              Community settings
            </MenuItem>
          )}
          {perms.manageRoles && (
            <MenuItem icon={IconShield} onSelect={() => navigate(`/c/${community.id}/settings/roles`)}>
              Roles
            </MenuItem>
          )}
          {perms.manageChannels && (
            <>
              <MenuItem icon={IconPlus} onSelect={() => setChannelDialog({ open: true, categoryId: null })}>
                Create channel
              </MenuItem>
              <MenuItem icon={IconFolder} onSelect={() => setCategoryDialog({ open: true })}>
                Create category
              </MenuItem>
            </>
          )}
          {community.visibility === "public" && (
            <MenuItem icon={IconGlobe} onSelect={() => navigate(`/community/${community.slug}`)}>
              Public page
            </MenuItem>
          )}
          {!community.isOwner && (
            <>
              <MenuSeparator />
              <MenuItem icon={IconLogout} danger onSelect={() => void leave()}>
                Leave community
              </MenuItem>
            </>
          )}
        </MenuContent>
      </Menu>
      </div>

      <nav ref={navRef} aria-label="Channels" className={cn("scroll-y min-h-0 flex-1 px-2 py-3", drag && "select-none")}>
        {groups.map(({ category, channels: list }, gi) =>
          !category && list.length === 0 ? null : (
            <div key={category?.id ?? "none"} className={cn("mb-3", category && drag?.source.id === category.id && "opacity-40")} data-cat-block={category?.id ?? ""} data-index={gi - 1}>
              {category && (
                <CategoryContextMenu
                  structure={structure}
                  category={category}
                  collapsed={!!collapsed[category.id]}
                  onToggle={() => setCollapsed((c) => ({ ...c, [category.id]: !c[category.id] }))}
                  onCollapseAll={() => setCollapsed(Object.fromEntries(categories.map((c) => [c.id, true])))}
                  canManageChannels={perms.manageChannels}
                  canManagePermissions={perms.manageChannels || perms.manageRoles}
                  onCreateChannel={() => setChannelDialog({ open: true, categoryId: category.id })}
                  onCreateCategory={() => setCategoryDialog({ open: true })}
                >
                <div data-cat-row data-cat={category.id} className={cn("group flex h-7 items-center rounded-md pr-1", dropInto === category.id && "bg-surface-2")}>
                  <button
                    onClick={() => setCollapsed((c) => ({ ...c, [category.id]: !c[category.id] }))}
                    onPointerDown={(e) => canReorder && pointerDown(e, { kind: "category", id: category.id })}
                    onKeyDown={(e) => keyMove(e, { kind: "category", id: category.id })}
                    aria-expanded={!collapsed[category.id]}
                    aria-describedby={canReorder ? hintId : undefined}
                    className="flex flex-1 items-center gap-1 truncate px-1 text-xs font-semibold text-fg-3 [-webkit-touch-callout:none] hover:text-fg-2"
                  >
                    {collapsed[category.id] ? <IconChevronRight className="icon size-3" /> : <IconChevronDown className="icon size-3" />}
                    <span className="truncate">{category.name}</span>
                  </button>
                  {perms.manageChannels && (
                    <div className="hidden items-center group-hover:flex group-focus-within:flex">
                      <IconButton size="sm" label="Edit category" icon={IconSettings} className="!size-6" onClick={() => navigate(`/c/${community.id}/category/${category.id}/settings`)} />
                      <IconButton size="sm" label="Create channel" icon={IconPlus} className="!size-6" onClick={() => setChannelDialog({ open: true, categoryId: category.id })} />
                    </div>
                  )}
                </div>
                </CategoryContextMenu>
              )}
              {!collapsed[category?.id ?? ""] && (
                <ul className="space-y-px">
                  {list.map((ch, i) => (
                    <ChannelItem
                      key={ch.id}
                      channel={ch}
                      index={i}
                      active={ch.id === activeChannelId}
                      canManage={perms.manageChannels || perms.manageRoles}
                      canDelete={perms.manageChannels}
                      onEdit={() => navigate(`/c/${community.id}/${ch.id}/settings`)}
                      participants={voiceStates.filter((v) => v.channelId === ch.id)}
                      dnd={dndFor({ kind: "channel", id: ch.id })}
                    />
                  ))}
                  {category && list.length === 0 && (
                    <li data-ch-empty data-cat={category.id} className={cn("rounded-md px-2 py-1 text-xs text-fg-3", dropInto === category.id && "bg-surface-2 text-fg-2")}>
                      No channels
                    </li>
                  )}
                </ul>
              )}
            </div>
          ),
        )}
        {channels.length === 0 && <p className="px-2 text-sm text-fg-3">No channels you can see yet.</p>}
        {canReorder && (
          <>
            <span id={hintId} className="sr-only">
              Press Alt and the arrow keys to move.
            </span>
            <span role="status" aria-live="polite" className="sr-only">
              {announce}
            </span>
          </>
        )}
      </nav>
      {drag && (
        <DragLayer>
        {drag.target && drag.target.into === undefined && navRect && (
          <span aria-hidden className="pointer-events-none fixed z-[70] h-0.5 rounded-full bg-accent" style={{ top: drag.target.y - 1, left: navRect.left + 8, width: navRect.width - 16 }} />
        )}
        <div
          aria-hidden
          className="pointer-events-none fixed left-0 top-0 z-[80] flex h-8 max-w-56 items-center gap-1.5 rounded-md bg-surface-2 px-2 text-base text-fg opacity-95 shadow-pop"
          style={{ transform: `translate(${drag.x + 12}px, ${drag.y - 16}px)` }}
        >
          {drag.source.kind === "category" ? (
            <IconFolder className="icon size-[18px] text-fg-3" />
          ) : (
            (() => {
              const ch = channels.find((c) => c.id === drag.source.id);
              const I = ch ? channelIcon(ch) : IconHash;
              return <I className="icon size-[18px] text-fg-3" />;
            })()
          )}
          <span className="truncate">{nameOf(drag.source)}</span>
        </div>
        </DragLayer>
      )}

      {community.timeoutUntil && new Date(community.timeoutUntil) > new Date() && (
        <div className="mx-2 mb-2 flex items-center gap-2 rounded-md bg-danger-soft px-2.5 py-2 text-xs text-danger">
          <IconTimer className="icon size-4 shrink-0" aria-hidden />
          Timed out until {new Date(community.timeoutUntil).toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short" })}
        </div>
      )}
      <UserPanel me={me} />

      <ChannelDialog
        open={channelDialog.open}
        onOpenChange={(o) => setChannelDialog((s) => ({ ...s, open: o }))}
        structure={structure}
        channel={channelDialog.channel}
        defaultCategoryId={channelDialog.categoryId ?? null}
      />
      <CategoryDialog open={categoryDialog.open} onOpenChange={(o) => setCategoryDialog((s) => ({ ...s, open: o }))} communityId={community.id} category={categoryDialog.category} />
      <InviteDialog open={inviteOpen} onOpenChange={setInviteOpen} community={community} />
    </div>
  );
}
